import React, { useEffect } from 'react';

type SEOProps = {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  locale?: string; // e.g., en_US, it_IT
  type?: 'website' | 'article';
  publishedTime?: string; // ISO for articles
  modifiedTime?: string; // ISO for articles
  jsonLd?: Record<string, any>;
};

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  image,
  locale = 'en_US',
  type = 'website',
  publishedTime,
  modifiedTime,
  jsonLd,
}) => {
  useEffect(() => {
    // Title
    if (title) {
      document.title = title;
    }

    // Helpers to set or update tags in <head>
    const ensureMetaByName = (name: string, content?: string) => {
      if (!content) return;
      let tag = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    const ensureMetaByProperty = (property: string, content?: string) => {
      if (!content) return;
      let tag = document.head.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    const ensureLink = (rel: string, href?: string) => {
      if (!href) return;
      let tag = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        document.head.appendChild(tag);
      }
      tag.setAttribute('href', href);
    };

    // Standard meta and canonical
    ensureMetaByName('description', description);
    ensureLink('canonical', canonical);

    // Open Graph
    ensureMetaByProperty('og:title', title);
    ensureMetaByProperty('og:description', description);
    ensureMetaByProperty('og:type', type);
    if (canonical) ensureMetaByProperty('og:url', canonical);
    ensureMetaByProperty('og:locale', locale);
    if (image) ensureMetaByProperty('og:image', image);

    // Twitter
    ensureMetaByName('twitter:card', image ? 'summary_large_image' : 'summary');
    ensureMetaByName('twitter:title', title);
    ensureMetaByName('twitter:description', description);
    if (image) ensureMetaByName('twitter:image', image);

    // Article specifics
    if (type === 'article') {
      if (publishedTime) ensureMetaByProperty('article:published_time', publishedTime);
      if (modifiedTime) ensureMetaByProperty('article:modified_time', modifiedTime);
    }

    // JSON-LD structured data
    const scriptId = 'seo-jsonld';
    const existing = document.getElementById(scriptId);
    if (jsonLd) {
      const serialized = JSON.stringify(jsonLd);
      if (existing) {
        existing.textContent = serialized;
      } else {
        const script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        script.text = serialized;
        document.head.appendChild(script);
      }
    } else if (existing) {
      existing.remove();
    }
  }, [title, description, canonical, image, locale, type, publishedTime, modifiedTime, jsonLd]);

  return null;
};

export default SEO;


